/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useContext, useState } from 'react';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import Scenario from '../../../domain/models/Scenario';
import getScenariosByDomainId from '../../../domain/use_cases/scenarios/getScenariosByDomainId';
import getScenarioById from '../../../domain/use_cases/scenarios/getScenarioById';
import getScenarios from '../../../domain/use_cases/scenarios/getScenarios';
import deleteScenario, { DeleteScenarioFailure } from '../../../domain/use_cases/scenarios/deleteScenario';
import AuthenticationContext from '../auth/AuthenticationContext';
import { generateDuplicatedName } from '../../../core/utils/commonUtils';
import createScenario from '../../../domain/use_cases/scenarios/createScenario';
import getScenariosByIds from '../../../domain/use_cases/scenarios/getScenariosByIds';
import { formatErrorMessage } from '../../../core/utils/errorUtils';

interface ScenariosContextProps {
  allScenarios: Scenario[];
  scenarios: Scenario[];
  domainId: string | null;
  selected: Scenario | null;
  fetchAll: () => Promise<void>;
  fetch: (domainId: string) => Promise<void>;
  fetchAndSelectById: (id: string) => Promise<void>;
  fetchByIds: (ids: string[]) => Promise<void>;
  submitDelete: (scenario: Scenario) => Promise<void>;
  submitDuplicate: (scenario: Scenario) => Promise<void>;
  select: (scenario: Scenario) => void;
  reset: () => void;
  errorMessage: string,
  status: ContextStatus;
  selectionStatus: ContextStatus;
  duplicateStatus: ContextStatus;
  deleteStatus: ContextStatus;
}

const ScenariosContext = createContext<ScenariosContextProps>({
  allScenarios: [],
  scenarios: [],
  domainId: null,
  selected: null,
  fetchAll: async () => { },
  fetch: async () => { },
  fetchByIds: async () => { },
  reset: () => { },
  fetchAndSelectById: async () => { },
  submitDelete: async () => { },
  submitDuplicate: async () => { },
  select: () => { },
  errorMessage: '',
  status: ContextStatus.IDLE,
  selectionStatus: ContextStatus.IDLE,
  duplicateStatus: ContextStatus.IDLE,
  deleteStatus: ContextStatus.IDLE,
});

export const ScenariosProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [allScenarios, setAllScenarios] = useState<Scenario[]>([]);
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [domainId, setDomainId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Scenario | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [status, setStatus] = useState<ContextStatus>(ContextStatus.IDLE);
  const [selectionStatus, setSelectionStatus] = useState<ContextStatus>(
    ContextStatus.IDLE
  );
  const [duplicateStatus, setDuplicateStatus] = useState<ContextStatus>(ContextStatus.IDLE);
  const [deleteStatus, setDeleteStatus] = useState<ContextStatus>(ContextStatus.IDLE);

  const authContext = useContext(AuthenticationContext);

  const fetchAll = async () => {
    setStatus(ContextStatus.LOADING);
    try {
      const fetchedScenarios = await getScenarios();
      setAllScenarios(fetchedScenarios);
      setStatus(ContextStatus.SUCCESS);
    } catch (error) {
      console.error('[ScenariosContext] fetch failed:', error);
      setScenarios([]);
      setStatus(ContextStatus.ERROR);
    }
  }

  const fetch = async (newDomainId: string) => {
    setDomainId(newDomainId);
    setStatus(ContextStatus.LOADING);

    try {
      const { scenarios: fetchedScenarios } = await getScenariosByDomainId(
        newDomainId
      );
      setScenarios(fetchedScenarios);
      setStatus(ContextStatus.SUCCESS);
    } catch (error) {
      console.error('[ScenariosContext] fetch failed:', error);
      setScenarios([]);
      setStatus(ContextStatus.ERROR);
    }
  };

  const fetchAndSelectById = async (id: string) => {
    setSelectionStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getScenarioById(id);
      if (useCaseResponse) {
        setSelected(useCaseResponse);
        setSelectionStatus(ContextStatus.SUCCESS);
      } else {
        setSelectionStatus(ContextStatus.ERROR);
      }
    } catch (e) {
      setSelectionStatus(ContextStatus.ERROR);
    }
  };

  const fetchByIds = async (ids: string[]) => {
    setStatus(ContextStatus.LOADING);
    try {
      const scenarios = await getScenariosByIds(ids);
      setScenarios(scenarios);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const submitDelete = async (scenario: Scenario) => {
    if (authContext.authentication === null) {
      return;
    }
    setDeleteStatus(ContextStatus.LOADING);
    try {
      await deleteScenario(scenario, authContext.authentication);
      setDeleteStatus(ContextStatus.SUCCESS);
    } catch (e) {
      if (e instanceof DeleteScenarioFailure) {
        setErrorMessage(formatErrorMessage(e.message));
      }
      setDeleteStatus(ContextStatus.ERROR);
    }
  };

  const submitDuplicate = async (scenario: Scenario) => {
    if (authContext.authentication === null) {
      return;
    }
    setDuplicateStatus(ContextStatus.LOADING);
    try {
      const scenarioNames = scenarios.map((s) => s.name);
      const duplicatedName = generateDuplicatedName(scenario.name, scenarioNames);

      const duplicatedScenario = new Scenario(
        Scenario.generateScenarioId(),
        scenario.idDomain,
        duplicatedName,
        scenario.nameShort,
        scenario.businessPriority,
        scenario.description,
        scenario.constraints,
        scenario.criteria,
        scenario.target,
        scenario.behavior,
        scenario.escalationRules,
        scenario.escalationNeedsQualification || false,
        scenario.fidelityModelFirstMessage || true,
        scenario.qualificationInstructions || "",
        scenario.samples,
        new Date(),
        new Date()
      )
      await createScenario(duplicatedScenario, authContext.authentication);
      setDuplicateStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setDuplicateStatus(ContextStatus.ERROR);
    }
  }

  const select = (scenario: Scenario) => {
    setSelected(scenario);
    setSelectionStatus(ContextStatus.SUCCESS);
  };

  const reset = () => {
    setScenarios([]);
    setDomainId(null);
    setSelected(null);
    setStatus(ContextStatus.IDLE);
    setSelectionStatus(ContextStatus.IDLE);
    setDuplicateStatus(ContextStatus.IDLE);
    setDeleteStatus(ContextStatus.IDLE);
  };

  return (
    <ScenariosContext.Provider
      value={{
        allScenarios,
        scenarios,
        domainId,
        selected,
        fetchAll,
        fetch,
        fetchAndSelectById,
        fetchByIds,
        submitDelete,
        submitDuplicate,
        select,
        reset,
        errorMessage,
        status,
        selectionStatus,
        duplicateStatus,
        deleteStatus
      }}
    >
      {children}
    </ScenariosContext.Provider>
  );
};

export default ScenariosContext;
